import type { ElementInfo } from '../types/observations';
import {
  isElementVisible,
  isElementEnabled,
  getElementPosition,
  getElementText,
  getElementValue,
  getElementAttributes,
  isInteractiveElement,
  getElementType
} from '../utils/dom-utils';
import { generateSelector, optimizeSelector } from '../utils/selector-utils';

export class ElementSelector {
  /**
   * Get all interactive elements on the page
   */
  getInteractiveElements(): ElementInfo[] {
    const elements: ElementInfo[] = [];
    const interactiveSelectors = [
      'a[href]',
      'button',
      'input',
      'select',
      'textarea',
      '[role="button"]',
      '[role="link"]',
      '[onclick]',
      '[tabindex]'
    ];

    const foundElements = new Set<Element>();

    for (const selector of interactiveSelectors) {
      const matches = document.querySelectorAll(selector);
      matches.forEach(el => foundElements.add(el));
    }

    foundElements.forEach(element => {
      if (isInteractiveElement(element)) {
        const info = this.getElementInfo(element);
        if (info) {
          elements.push(info);
        }
      }
    });

    return elements;
  }

  /**
   * Get information about a specific element
   */
  getElementInfo(element: Element): ElementInfo | null {
    try {
      const selector = this.generateUniqueSelector(element);

      return {
        selector,
        tagName: element.tagName.toLowerCase(),
        type: getElementType(element) as any,
        text: getElementText(element),
        value: getElementValue(element),
        attributes: getElementAttributes(element),
        position: getElementPosition(element),
        isVisible: isElementVisible(element),
        isEnabled: isElementEnabled(element)
      };
    } catch (error) {
      console.error('Failed to get element info:', error);
      return null;
    }
  }

  /**
   * Generate a unique, stable selector for an element
   */
  generateUniqueSelector(element: Element): string {
    const selector = generateSelector(element);
    return optimizeSelector(selector);
  }

  /**
   * Find element by selector
   */
  findElement(selector: string): Element | null {
    try {
      return document.querySelector(selector);
    } catch {
      return null;
    }
  }

  /**
   * Find all elements by selector
   */
  findElements(selector: string): Element[] {
    try {
      return Array.from(document.querySelectorAll(selector));
    } catch {
      return [];
    }
  }

  /**
   * Get visible elements only
   */
  getVisibleElements(elements: ElementInfo[]): ElementInfo[] {
    return elements.filter(el => el.isVisible);
  }

  /**
   * Get enabled elements only
   */
  getEnabledElements(elements: ElementInfo[]): ElementInfo[] {
    return elements.filter(el => el.isEnabled);
  }

  /**
   * Filter elements by type
   */
  getElementsByType(elements: ElementInfo[], type: string): ElementInfo[] {
    return elements.filter(el => el.type === type);
  }
}
